import {useQuery} from '@tanstack/react-query';
import {AxiosResponse} from 'axios';
import uniq from 'lodash/uniq';
import {useGetStacCollections} from './catalog.ts';
import urls from './urls.js';
import {DURATION_MS, getQueryKeys} from './util.js';
import {request} from './index.js';
import {
  configurationPublicDataTemplateId,
  configurationSandboxDataTemplateId,
  isOpenCollection,
  isPlanetSandboxCollection,
} from '@components/dataCollections/Collections/openCollections.tsx';

import {SUPER_RES_LAYER_ID} from '@src/constants/analyze.ts';
import {
  Configuration,
  Layer,
  StacCollection,
} from '@src/types/configurations.ts';

export const DEFAULT_CONFIGURATIONS: Configuration[] = [
  {
    name: 'Planet Sandbox Data',
    id: 'f1ff2454-067f-4d01-9f19-63f21d763817',
    readOnly: true,
  },
  {
    name: 'Public data (featured collections)',
    id: '60de79ca-16a7-4afd-bcbd-0261bf0156fa',
    readOnly: true,
  },
];

const getConfigurations = (collectionId?: string, projectId?: string) => {
  return request
    .get<Configuration[]>(`${urls.configuration}/instances`, {
      params: {
        collectionId,
        domainAccountId: projectId,
      },
    })
    .then((res: AxiosResponse<Configuration[]>) => res.data);
};

const getConfiguration = (configurationId: string) => {
  return request
    .get<Configuration>(`${urls.configuration}/instances/${configurationId}`)
    .then((res: AxiosResponse<Configuration>) => res.data);
};

export const getConfigurationLayers = (configurationId: string) => {
  const url = new URL(
    `${urls.configuration}/instances/${configurationId}/layers`,
  );
  url.searchParams.append('listHighlights', 'true');
  return request
    .get<Layer[]>(url.toString())
    .then((res: AxiosResponse<Layer[]>) => res.data);
};

const getConfigurationLayer = (configurationId: string, layerId: string) => {
  if (layerId === SUPER_RES_LAYER_ID) {
    return new Promise<Layer>(resolve => {
      resolve({
        id: SUPER_RES_LAYER_ID,
        title: 'SuperRes',
        instanceId: configurationId,
        collectionId: '',
        collectionType: '',
        lastUpdated: '',
        defaultStyleName: '',
        orderHint: 0,
        datasetSourceId: 0,
        datasourceDefaults: {},
        styles: [
          {
            name: 'SuperRes',
            evalScript: '',
            advancedEvalScript: '69d62592-3aaa-4d37-a59a-2d3aefe52b49',
            evalscriptParams: {
              buffer: 20,
              blockSize: 216,
            },
          },
        ],
      });
    });
  }
  return request
    .get<Layer>(
      `${urls.configuration}/instances/${configurationId}/layers/${layerId}`,
    )
    .then((res: AxiosResponse<Layer>) => res.data);
};

const configurationsQueryKeys = getQueryKeys('configurations');
const layersQueryKeys = getQueryKeys('layers');

export const mergeWithDefaults = (configurations: Configuration[]) =>
  [...DEFAULT_CONFIGURATIONS, ...configurations].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

export const useGetConfigurations = <T = Configuration[]>(options?: {
  select?: (data: Configuration[]) => T;
}) => {
  return useQuery<Configuration[], Error, T>({
    queryKey: configurationsQueryKeys.lists(),
    queryFn: () => getConfigurations(),
    select: data => {
      const newData = [...data].sort((a, b) => a.name.localeCompare(b.name));
      if (options?.select) {
        return options.select(newData);
      }
      return newData as T;
    },
    staleTime: DURATION_MS.MINUTE,
  });
};

export const useGetConfiguration = (configurationId: string) => {
  return useQuery<Configuration>({
    queryKey: configurationsQueryKeys.detail(configurationId),
    queryFn: () => getConfiguration(configurationId),
    enabled: !!configurationId,
    staleTime: DURATION_MS.MINUTE,
  });
};

export const useGetConfigurationLayers = <T = Layer[]>(
  configurationId: string,
  options?: {select?: (data: Layer[]) => T},
) => {
  return useQuery<Layer[], Error, T>({
    queryKey: layersQueryKeys.list({configurationId}),
    queryFn: () => getConfigurationLayers(configurationId),
    enabled: !!configurationId,
    select: options?.select,
    staleTime: DURATION_MS.MINUTE,
  });
};

export const useGetConfigurationLayer = (
  configurationId?: string,
  layerId?: string,
) => {
  return useQuery<Layer>({
    queryKey: layersQueryKeys.detail({configurationId, layerId}),
    queryFn: () => getConfigurationLayer(configurationId!, layerId!),
    enabled: !!configurationId && !!layerId,
    staleTime: DURATION_MS.MINUTE,
  });
};

const mapLayersByCollectionIds = (layers: Layer[]) => {
  return uniq(layers.map(layer => layer.collectionId));
};

const filterAndSortLayersByCollectionId =
  (collectionId: string) => (layers?: Layer[]) => {
    const filteredLayers =
      layers?.filter(layer => layer.collectionId === collectionId) ?? [];

    return filteredLayers.sort(
      (a, b) => a.orderHint - b.orderHint || a.title.localeCompare(b.title),
    );
  };

export const useGetConfigurationCollections = (configurationId: string) => {
  const {
    data: collectionIds = [],
    isLoading: loadingCollectionIds,
    error: collectionIdsError,
  } = useGetConfigurationLayers(configurationId, {
    select: mapLayersByCollectionIds,
  });

  const collectionsResponse = useGetStacCollections(collectionIds);

  // Map responses to include collection IDs
  const collectionsWithIds = collectionsResponse.map((result, index) => ({
    collectionId: collectionIds[index],
    data: result.data,
    error: result.error,
    isLoading: result.isLoading,
  }));

  const collections = collectionsWithIds
    .filter(item => item.data)
    .map(item => item.data) as StacCollection[];

  const failedCollections = collectionsWithIds
    .filter(item => item.error)
    .map(item => item.collectionId);

  const isLoading =
    loadingCollectionIds ||
    collectionsResponse.some(result => result.isLoading);
  const collectionsError = collectionsResponse.every(result => result.error);
  const isError = !isLoading && (collectionsError || !!collectionIdsError);
  return {
    collections,
    isLoading,
    error:
      collectionIdsError ||
      (isError ? new Error('Collections failed to load') : undefined),
    isError,
    failedCollections,
  };
};

export const useGetCollectionLayers = (
  configurationId: string,
  collectionId: string,
) => {
  const {
    data: layers,
    isLoading,
    error,
  } = useGetConfigurationLayers(configurationId, {
    select: filterAndSortLayersByCollectionId(collectionId),
  });

  return {
    layers,
    isLoading,
    error,
  };
};

export const fetchCollectionConfigurations = async (
  collectionId: string,
  projectId: string,
): Promise<Configuration[]> => {
  const instances = await getConfigurations(collectionId, projectId);

  if (isPlanetSandboxCollection(collectionId)) {
    const planetSandBoxData = await getPlanetSandboxDataInstance();
    if (
      planetSandBoxData.layers?.find(
        (layer: Layer) =>
          layer.datasourceDefaults.collectionId === collectionId,
      )
    ) {
      return [planetSandBoxData, ...instances];
    }
  }

  if (isOpenCollection(collectionId)) {
    const publicData = await getPublicDataInstance();
    if (
      publicData.layers?.find(
        (layer: Layer) => layer.collectionId === collectionId,
      )
    ) {
      return [publicData, ...instances];
    }
  }

  return instances;
};

export const useCollectionConfigurations = <T = Configuration[]>(
  collectionId: string,
  projectId: string,
) => {
  return useQuery<Configuration[], Error, T>({
    queryKey: ['collectionConfigurations', collectionId, projectId],
    queryFn: () => fetchCollectionConfigurations(collectionId, projectId),
    staleTime: DURATION_MS.HOUR,
  });
};

async function getPlanetSandboxDataInstance() {
  const [instanceResp, layersResp] = await Promise.all([
    request.get(
      urls.configuration + '/instances/' + configurationSandboxDataTemplateId,
    ),
    request.get(
      urls.configuration +
        '/instances/' +
        configurationSandboxDataTemplateId +
        '/layers',
    ),
  ]);

  instanceResp.data.layers = layersResp.data;
  instanceResp.data.name = 'Planet Sandbox Data';
  return instanceResp.data;
}

async function getPublicDataInstance() {
  const [instanceResp, layersResp] = await Promise.all([
    request.get(
      urls.configuration + '/instances/' + configurationPublicDataTemplateId,
    ),
    request.get(
      urls.configuration +
        '/instances/' +
        configurationPublicDataTemplateId +
        '/layers',
    ),
  ]);

  instanceResp.data.layers = layersResp.data;
  instanceResp.data.name = 'Public Data (featured collections)';
  return instanceResp.data;
}

export async function addConfiguration(
  templateId: string,
  domainAccountId: string,
  name: string,
) {
  const response = await request.post(
    `https://services.sentinel-hub.com/api/v2/configuration/instances/${templateId}/clone?to_domain_account_id=${domainAccountId}`,
    {
      id: templateId,
      name,
      domainAccountId,
    },
  );

  return response.data as Configuration;
}

export async function addConfigurationLayers(
  instanceId: string,
  layers: Layer[],
  collectionId: string,
) {
  const promises = layers.map((layer: Layer) => {
    const newLayer = {
      ...layer,
      datasourceDefaults: {
        ...layer.datasourceDefaults,
        collectionId,
      },
    };

    return request.put(
      `https://services.sentinel-hub.com/api/v2/configuration/instances/${instanceId}/layers/${layer.id}`,
      newLayer,
    );
  });

  await Promise.all(promises);
}
